/* ==========================================================================
   FreezeCap™ Vehicle Compatibility Database
   --------------------------------------------------------------------------
   Determines whether a US-market passenger vehicle uses R-134a or R-1234yf.

   Default rule (covers ~95% of vehicles):
     • model year <= 2012  →  R-134a (high confidence)
     • model year >= 2021  →  R-1234yf (high confidence — EPA mandate)
     • model year 2013–2020 → verify (mixed transition window)

   Source: docs/freezecap-product-mechanics.md "Refrigerant Identification"
   section. Some manufacturers (notably Chrysler/FCA) adopted R-1234yf as
   early as 2014; most major OEMs had transitioned by 2017–2019. The
   "verify" branch returns R-134a as the most-likely value for early
   transition years but flags low confidence.

   Exceptions below override the default rule with high-confidence answers
   for specific make/model combinations whose refrigerant type IS known.
   Sources: EPA SNAP Rule 20 Final List, manufacturer service bulletins,
   RepairPal, AllData public summaries. Always recommend the user verify
   via owner's manual, under-hood sticker, or VIN lookup before ordering.

   Localization: the 4 default-rule returns carry a stable `noteId`
   (le2012 / ge2021 / verify / invalid) so fc-fit.js can swap in a
   translated note from window.FC_FIT_I18N. The make/model exception notes
   are left as English text (rare cases; fc-fit.js falls back to `note`).
   ========================================================================== */

(function () {
  var makes = [
    'Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler',
    'Dodge', 'Fiat', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'Infiniti',
    'Jaguar', 'Jeep', 'Kia', 'Land Rover', 'Lexus', 'Lincoln', 'Mazda',
    'Mercedes-Benz', 'Mini', 'Mitsubishi', 'Nissan', 'Pontiac', 'Porsche',
    'Ram', 'Saturn', 'Scion', 'Subaru', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo'
  ];

  /* Each exception is { make, models?, yearStart, yearEnd, refrigerant, note }.
     If `models` is omitted, the rule applies to the entire make for the range.
     Models are matched case-insensitively as substrings of the user input. */
  var exceptions = [
    // ── Early R-1234yf adopters (before the 2017 transition) ────────────────
    { make: 'Cadillac',  models: ['XTS', 'ATS'],            yearStart: 2013, yearEnd: 2016, refrigerant: 'R-1234yf', note: 'Cadillac was an early R-1234yf adopter on select 2013–2016 platforms.' },
    { make: 'Chevrolet', models: ['Spark EV', 'Trax'],      yearStart: 2013, yearEnd: 2016, refrigerant: 'R-1234yf', note: 'GM transitioned several 2013–2016 models early.' },
    { make: 'Chrysler',  models: ['300'],                   yearStart: 2013, yearEnd: 2016, refrigerant: 'R-1234yf', note: 'Chrysler 300 began R-1234yf in 2013.' },
    { make: 'Dodge',     models: ['Challenger', 'Charger', 'Dart', 'Durango'], yearStart: 2013, yearEnd: 2016, refrigerant: 'R-1234yf', note: 'FCA rolled out R-1234yf on several 2013+ platforms.' },
    { make: 'Jeep',      models: ['Cherokee', 'Grand Cherokee'], yearStart: 2014, yearEnd: 2016, refrigerant: 'R-1234yf', note: 'Cherokee/Grand Cherokee adopted R-1234yf early.' },
    { make: 'Ram',       models: ['ProMaster'],             yearStart: 2014, yearEnd: 2016, refrigerant: 'R-1234yf', note: 'ProMaster van adopted R-1234yf in 2014.' },
    { make: 'Honda',     models: ['Fit EV'],                yearStart: 2013, yearEnd: 2014, refrigerant: 'R-1234yf', note: 'Honda Fit EV used R-1234yf.' },
    { make: 'Subaru',    models: ['Crosstrek Hybrid'],      yearStart: 2014, yearEnd: 2016, refrigerant: 'R-1234yf', note: 'Subaru Crosstrek Hybrid adopted R-1234yf early.' },
    { make: 'Hyundai',   models: ['Sonata Hybrid'],         yearStart: 2015, yearEnd: 2016, refrigerant: 'R-1234yf', note: 'Hyundai Sonata Hybrid 2015+ uses R-1234yf.' },
    { make: 'Kia',       models: ['Optima Hybrid'],         yearStart: 2015, yearEnd: 2016, refrigerant: 'R-1234yf', note: 'Kia Optima Hybrid 2015+ uses R-1234yf.' },

    // ── Late R-134a holdouts (after most makes had transitioned) ─────────────
    { make: 'Ford',      models: ['F-250', 'F-350', 'F-450', 'F-550', 'Super Duty'], yearStart: 2017, yearEnd: 2019, refrigerant: 'R-134a', note: 'Ford Super Duty trucks held R-134a longer than light-duty F-150.' },
    { make: 'Ram',       models: ['2500', '3500', '4500', '5500', 'Heavy Duty'], yearStart: 2017, yearEnd: 2019, refrigerant: 'R-134a', note: 'Ram heavy-duty trucks transitioned later.' },
    { make: 'Chevrolet', models: ['Silverado 2500', 'Silverado 3500'], yearStart: 2017, yearEnd: 2019, refrigerant: 'R-134a', note: 'GM heavy-duty trucks held R-134a into 2019.' },
    { make: 'GMC',       models: ['Sierra 2500', 'Sierra 3500'],       yearStart: 2017, yearEnd: 2019, refrigerant: 'R-134a', note: 'GM heavy-duty trucks held R-134a into 2019.' },

    // ── EVs and outliers ─────────────────────────────────────────────────────
    { make: 'Tesla',     models: ['Model S', 'Model X'],    yearStart: 2012, yearEnd: 2020, refrigerant: 'R-1234yf', note: 'Tesla Model S/X have used R-1234yf since launch in most years; verify by VIN.' },
    { make: 'Tesla',     models: ['Model 3', 'Model Y'],    yearStart: 2017, yearEnd: 2030, refrigerant: 'R-1234yf', note: 'Tesla Model 3 and Y use R-1234yf.' }
  ];

  function normalize(s) { return (s || '').toString().trim().toLowerCase(); }

  function matchModel(userModel, candidateModels) {
    if (!candidateModels || !candidateModels.length) return true;
    var u = normalize(userModel);
    if (!u) return false;
    for (var i = 0; i < candidateModels.length; i++) {
      if (u.indexOf(normalize(candidateModels[i])) !== -1) return true;
      if (normalize(candidateModels[i]).indexOf(u) !== -1) return true;
    }
    return false;
  }

  /*
    Returns:
      { refrigerant: 'R-134a' | 'R-1234yf' | null,
        confidence: 'high' | 'medium' | 'verify',
        noteId?: string,   // present for default-rule results (localizable)
        note: string }
  */
  function getRefrigerantDetailed(year, make, model) {
    var y = parseInt(year, 10);
    if (isNaN(y) || y < 1994 || y > 2030) {
      return { refrigerant: null, confidence: 'none', noteId: 'invalid', note: 'Enter a valid model year between 1994 and 2030.' };
    }

    var makeNorm = normalize(make);

    // Apply exceptions first
    if (makeNorm) {
      for (var i = 0; i < exceptions.length; i++) {
        var ex = exceptions[i];
        if (normalize(ex.make) !== makeNorm) continue;
        if (y < ex.yearStart || y > ex.yearEnd) continue;
        if (matchModel(model, ex.models)) {
          return {
            refrigerant: ex.refrigerant,
            confidence: model ? 'high' : 'medium',
            note: ex.note + ' Always confirm via owner\'s manual or under-hood sticker.'
          };
        }
      }
    }

    // Default rule
    if (y <= 2012) {
      return {
        refrigerant: 'R-134a',
        confidence: 'high',
        noteId: 'le2012',
        note: 'Vehicles built 2012 and earlier almost universally use R-134a.'
      };
    }
    if (y >= 2021) {
      return {
        refrigerant: 'R-1234yf',
        confidence: 'high',
        noteId: 'ge2021',
        note: '2021 and newer vehicles use R-1234yf — federally mandated for new US light-duty platforms.'
      };
    }
    // 2013–2020 mixed transition window
    return {
      refrigerant: 'R-134a',
      confidence: 'verify',
      noteId: 'verify',
      note: '2013–2020 is the manufacturer transition window. Some makes (notably Chrysler/FCA) switched as early as 2014; most major OEMs had transitioned by 2017–2019. Verify via the A/C system sticker, your owner\'s manual, or a VIN lookup before ordering.'
    };
  }

  window.FreezeCapCompat = {
    yearRange: { min: 1994, max: 2030 },
    makes: makes,
    exceptions: exceptions,
    getRefrigerant: function (year) {
      // Backwards-compatible simple lookup
      var r = getRefrigerantDetailed(year);
      return r.refrigerant;
    },
    getRefrigerantDetailed: getRefrigerantDetailed,
    isCompatible: function (year) {
      var y = parseInt(year, 10);
      return !isNaN(y) && y >= 1994 && y <= 2030;
    }
  };
})();
